/**
 * Eine Zeile der Dokumentliste — geteilt zwischen dem Reiter *Dokumente*
 * eines Objekts und dem Bereich *Dokumente* (Spec 09, „Was neu entsteht":
 * „Bereich und Reiter teilen sich eine Listenkomponente — dieselbe
 * Aufteilung wie `ContractList`"). Spaltenbau nach dem Vorbild von
 * `features/billing/OpenItemsList.tsx` (`COLUMN`, eine Quelle für Kopf und
 * Zeile).
 *
 * **Bei 380px** wird die Zeile zur Karte (Spec 35): Art-Symbol links, Titel
 * und Betrag, darunter „Art · Absender" mit dem Kennsatz rechts, zuletzt
 * „Objekt · Datum".
 * Das ist eine andere **Anordnung**, nicht nur andere Breiten — deshalb
 * zwei getrennte Kompositionen über `useIsDesktopViewport()` statt eines
 * gemeinsamen `md:contents`-Umbruchs; zwei nur optisch versteckte Kopien
 * (`hidden md:flex`) stünden sonst mit denselben `data-testid`-Werten
 * doppelt im Dokument (`lib/viewport.ts`, dieselbe Begründung).
 *
 * Ein Klick auf die Zeile öffnet `document-sheet` zum Ändern — dieselbe
 * Sheet-Instanz trägt bei einem noch nicht zugeordneten Dokument den
 * Suchmodus von `DocumentSubjectField` (Spec 09, „Zuordnen ist editor, nicht
 * manager"): `document-assign-<id>` ist ein zweiter, hervorgehobener
 * Auslöser für **dasselbe** Sheet, keine eigene Ansicht.
 */

import { categoryIcon, documentKindLine } from '@/features/documents/document-kind'
import { documentHintBadge, documentSubjectLabel } from '@/features/documents/labels'
import type { DocumentHintBadge } from '@/features/documents/labels'
import type { DocumentRow as DocumentRowData } from '@/features/documents/api'
import { formatDate } from '@/lib/date'
import { useIsDesktopViewport } from '@/lib/viewport'

interface DocumentRowProps {
  readonly document: DocumentRowData
  readonly onOpen: () => void
  readonly onAssign: () => void
  /** Karte statt Tabellenzeile, auch am Schreibtisch — für schmale Spalten wie
   * das Zählerdetail neben der Liste (Spec 46c), deren Breite die sechs
   * Spalten der Tabelle nicht trägt (der Titel bekäme 0 px). */
  readonly compact?: boolean
}

export function DocumentRow({ document, onOpen, onAssign, compact = false }: DocumentRowProps) {
  const isDesktop = useIsDesktopViewport() && !compact
  const subjectLabel = documentSubjectLabel(document.subject)
  const subjectKindLabel = document.subject?.kind?.label ?? null
  const isUnfiled = document.subject === null
  const hintBadge = documentHintBadge(document)

  return (
    <li
      data-testid={`document-row-${document.id}`}
      className={`group border-border flex flex-col border-b px-(--page-x) py-2 last:border-b-0 ${
        compact ? '' : 'md:h-(--row-h) md:flex-row md:items-center md:py-0'
      }`}
    >
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
        {isDesktop ? (
          <DesktopRow
            document={document}
            subjectLabel={subjectLabel}
            subjectKindLabel={subjectKindLabel}
            hintBadge={hintBadge}
          />
        ) : (
          <MobileCard
            document={document}
            subjectLabel={subjectLabel}
            subjectKindLabel={subjectKindLabel}
            hintBadge={hintBadge}
          />
        )}
      </button>

      {isUnfiled ? (
        <button
          type="button"
          data-testid={`document-assign-${document.id}`}
          onClick={(event) => {
            event.stopPropagation()
            onAssign()
          }}
          className={`${ASSIGN_SLOT_CLASS} border-border text-text hover:bg-bg mt-2 md:mt-0 md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100`}
        >
          Zuordnen…
        </button>
      ) : (
        // Reserviert am Schreibtisch dieselbe Breite wie der Knopf oben —
        // sonst schrumpft nur die `flex-1`-Spalte **zugeordneter** Zeilen
        // nicht um die (unsichtbare) Knopfbreite, während unzugeordnete
        // Zeilen und der Kopf (`DocumentListHeader`) das tun: In „Ohne
        // Zuordnung" betraf das jede Zeile gegen den Kopf, in „Alle" schon
        // zugeordnete gegen unzugeordnete Zeilen (W-D2,
        // `reports/18-design-reviewer.md`). Gleicher Text/gleiche Klassen
        // statt einer geschätzten Breite, damit beide garantiert
        // übereinstimmen — `md:invisible` behält die Box, verwirft nur die
        // Darstellung; unter 768px `hidden`, weil zugeordnete Zeilen dort
        // schon vorher nichts zeigten.
        !compact && (
          <span aria-hidden="true" className={ASSIGN_PLACEHOLDER_CLASS}>
            Zuordnen…
          </span>
        )
      )}
    </li>
  )
}

interface RowContentProps {
  readonly document: DocumentRowData
  readonly subjectLabel: string
  readonly subjectKindLabel: string | null
  readonly hintBadge: DocumentHintBadge
}

/** Spec 35, Fachregel 4: die Art als Symbol in neutraler Kachel. Das Wort
 * steht in der Zweitzeile (`documentKindLine`), deshalb ist das Symbol
 * dekorativ. Nie über Farbe unterschieden. */
function KindTile({ document }: { document: DocumentRowData }) {
  return (
    <span className="bg-border/40 text-muted rounded-card flex size-7 shrink-0 items-center justify-center">
      {categoryIcon(document.category)}
    </span>
  )
}

/** Spec 24c, Oberfläche D: derselbe Kennsatz wie „nicht durchsuchbar", nur in
 * `accent` statt `muted` und ohne Punkt — ein Vorschlag ist kein Rückstand.
 * Beide schließen sich aus (`documentHintBadge`), deshalb ein
 * `HintBadge`-Renderer statt zweier paralleler Bedingungen an jeder
 * Aufruferstelle. */
function HintBadge({ documentId, badge }: { documentId: string; badge: NonNullable<DocumentHintBadge> }) {
  const isSuggestion = badge.kind === 'suggestion'
  return (
    <span
      data-testid={`document-row-${isSuggestion ? 'suggestion' : 'nosearch'}-${documentId}`}
      // Schmal steht der Kennsatz in einer eigenen Zeile unter „Art · Absender“
      // (wie auf iOS, Spec 35, Designreview D1): so verdrängt „nicht
      // durchsuchbar · wird gelesen …“ weder Titel noch bricht er mitten im
      // Status um. Am Schreibtisch bleibt er einzeilig in der Hinweisspalte.
      className={`rounded-card shrink-0 self-start border px-1.5 py-0.5 text-left text-label md:whitespace-nowrap ${
        isSuggestion ? 'border-accent text-accent' : 'border-border text-muted'
      }`}
    >
      {badge.label}
    </span>
  )
}

/** Spaltenbreiten am Schreibtisch — eine Quelle für Kopf (`DocumentListHeader`
 * in `DocumentList.tsx`) und Zeile. */
export const DOCUMENT_COLUMN = {
  symbol: 'md:w-12',
  document: 'md:flex-1',
  subject: 'md:w-44',
  date: 'md:w-28',
  amount: 'md:w-28',
  hint: 'md:w-36',
} as const

/** Form von `document-assign-<id>` — geteilt mit seinem unsichtbaren
 * Platzhalter (W-D2, siehe unten) und dem Kopf-Gegenstück in
 * `DocumentListHeader`, damit alle drei ohne geschätzte Breite exakt
 * übereinstimmen. */
export const ASSIGN_SLOT_CLASS =
  'rounded-card text-label flex min-h-(--tap-min) shrink-0 items-center justify-center border px-3 md:min-h-(--tap-min) md:ml-3'

/** Unsichtbar, aber raumgreifend (`md:invisible`, nicht `md:opacity-0`) —
 * derselbe Text wie der echte Knopf, damit die Breite nicht geschätzt werden
 * muss. Unter 768px `hidden`: Dort zeigte eine bereits zugeordnete Zeile noch
 * nie etwas an dieser Stelle. */
export const ASSIGN_PLACEHOLDER_CLASS = `${ASSIGN_SLOT_CLASS} border-border hidden md:flex md:invisible`

function DesktopRow({ document, subjectLabel, subjectKindLabel, hintBadge }: RowContentProps) {
  const kindLine = documentKindLine(document)
  return (
    <div className="flex items-center gap-3">
      <span className={`flex justify-center ${DOCUMENT_COLUMN.symbol}`}>
        <KindTile document={document} />
      </span>
      <span className={`flex min-w-0 flex-col ${DOCUMENT_COLUMN.document}`}>
        <span className="text-body text-text truncate font-medium">{document.title}</span>
        {kindLine !== null && <span className="text-label text-muted truncate">{kindLine}</span>}
      </span>
      <span className={`flex min-w-0 flex-col ${DOCUMENT_COLUMN.subject}`}>
        <span data-testid={`document-row-subject-${document.id}`} className="text-label text-text truncate">
          {subjectLabel}
        </span>
        {subjectKindLabel !== null && <span className="text-label text-muted truncate">{subjectKindLabel}</span>}
      </span>
      <span className={`text-label text-muted ${DOCUMENT_COLUMN.date}`}>
        {document.document_date === null ? '—' : formatDate(document.document_date)}
      </span>
      <span data-numeric className={`text-body text-text ${DOCUMENT_COLUMN.amount}`}>
        {document.amount_display ?? '—'}
      </span>
      <span className={DOCUMENT_COLUMN.hint}>
        {hintBadge !== null && <HintBadge documentId={document.id} badge={hintBadge} />}
      </span>
    </div>
  )
}

function MobileCard({ document, subjectLabel, hintBadge }: RowContentProps) {
  const kindLine = documentKindLine(document)
  return (
    <div className="flex items-start gap-2.5">
      <KindTile document={document} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-body text-text truncate font-medium">{document.title}</span>
          {document.amount_display !== null && (
            <span data-numeric className="text-body text-text shrink-0">
              {document.amount_display}
            </span>
          )}
        </div>
        {kindLine !== null && <div className="text-label text-muted truncate">{kindLine}</div>}
        {hintBadge !== null && <HintBadge documentId={document.id} badge={hintBadge} />}
        <div className="text-label text-muted truncate">
          <span data-testid={`document-row-subject-${document.id}`}>{subjectLabel}</span>
          {document.document_date !== null && <span> · {formatDate(document.document_date)}</span>}
        </div>
      </div>
    </div>
  )
}
